import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';

import {
	Engine,
	Render,
	Bodies,
	World,
	Mouse,
	MouseConstraint,
	Composites,
	Body,
} from 'matter-js';
import { useLenis } from '@studio-freight/react-lenis';
import { throttle } from 'lodash';

import Emoji from '../util/emoji';
import Arrow from '../svg/arrow';
import CustomLink from '../util/link';
import Image from '../components/image';

const HomepageIntro = () => {
	const data = useStaticQuery(graphql`
		{
			homepage: datoCmsHomepage {
				intro {
					... on DatoCmsButton {
						__typename
						id
						text
					}
					... on DatoCmsEmoji {
						__typename
						id
						emoji
					}
					... on DatoCmsUniversal {
						__typename
						id
						text
					}
					... on DatoCmsFeature {
						__typename
						id
						text
					}
					... on DatoCmsLineBreak {
						__typename
						id
					}
				}
			}
			creators: datoCmsSubbrandJc {
				description
				medium: featuredImage {
					alt
					url(imgixParams: { auto: "compress", w: "480" })
				}
				loader: featuredImage {
					url(imgixParams: { auto: "compress", w: "24" })
				}
			}
			media: datoCmsSubbrandJm {
				description
				medium: featuredImage {
					alt
					url(imgixParams: { auto: "compress", w: "480" })
				}
				loader: featuredImage {
					url(imgixParams: { auto: "compress", w: "24" })
				}
			}
			wild: datoCmsSubbrandWild {
				description
				medium: featuredImage {
					alt
					url(imgixParams: { auto: "compress", w: "480" })
				}
				loader: featuredImage {
					url(imgixParams: { auto: "compress", w: "24" })
				}
			}
		}
	`);
	const homepage = data.homepage;
	const sectionRef = useRef(null);
	const buttonRef = useRef(null);
	const mediaRef = useRef(null);
	const creatorRef = useRef(null);
	const wildRef = useRef(null);
	const velocityRef = useRef(0);

	const heading = homepage.intro.map((item) => {
		switch (item.__typename) {
			case 'DatoCmsUniversal':
				return (
					<React.Fragment key={item.id}>{item.text}</React.Fragment>
				);
			case 'DatoCmsFeature':
				return (
					<span className='feature' key={item.id}>
						{item.text}
					</span>
				);
			case 'DatoCmsEmoji':
				return <Emoji key={item.id} type={item.emoji} />;
			case 'DatoCmsButton':
				return (
					<button
						className='btn btn--large show-emoji heading-feature-btn'
						ref={buttonRef}
					>
						<Emoji type='wave' />
						<span className='btn-text'>
							<span className='default'>
								<span>{item.text}</span>
							</span>
							<span className='hover'>
								<span>{item.text}</span>
							</span>
						</span>
					</button>
				);
			case 'DatoCmsLineBreak':
				return <br className='line-break' key={item.id} />;
			default:
				return null;
		}
	});

	const lenis = useLenis(({ scroll }) => {
		velocityRef.current = lenis.velocity;
	});
	const speed = 0.1;

	useEffect(() => {
		if (!sectionRef.current) {
			return;
		}

		// Initialize engine and renderer with the width and height of the section
		let w = sectionRef.current.offsetWidth;
		let h = sectionRef.current.offsetHeight;
		const engine = Engine.create();

		const renderer = Render.create({
			element: sectionRef.current,
			engine: engine,
			options: {
				width: w,
				height: h,
				background: 'transparent',
				wireframes: false,
				pixelRatio: window.devicePixelRatio,
			},
		});

		const createShape = function (x, y) {
			return Bodies.circle(x, y, 25, {
				frictionAir: 0.005,
				render: {
					sprite: {
						texture: '/images/heart.svg',
						yScale: 1.5,
						xScale: 1.5,
					},
				},
			});
		};

		const createWall = function (element) {
			const elementRect = element.getBoundingClientRect();
			const sectionRect = sectionRef.current.getBoundingClientRect();
			const relativeLeft = elementRect.left - sectionRect.left;
			const relativeTop = elementRect.top - sectionRect.top;

			// Create a static rounded rectangle body corresponding to the object
			return Bodies.rectangle(
				relativeLeft + elementRect.width / 2,
				relativeTop + elementRect.height / 2,
				elementRect.width,
				elementRect.height,
				{
					isStatic: true,
					chamfer: { radius: 24 },
					render: {
						visible: false,
					},
				}
			);
		};

		const buttonWall = createWall(buttonRef.current);
		const creatorWall = createWall(creatorRef.current);
		const mediaWall = createWall(mediaRef.current);
		const wildWall = createWall(wildRef.current);

		const wallOptions = {
			isStatic: true,
			render: {
				visible: false,
			},
		};

		const ground = Bodies.rectangle(
			w / 2,
			h + 50,
			w + 100,
			100,
			wallOptions
		);
		const ceiling = Bodies.rectangle(w / 2, -50, w + 100, 100, wallOptions);
		const leftWall = Bodies.rectangle(
			-50,
			h / 2,
			100,
			h + 100,
			wallOptions
		);
		const rightWall = Bodies.rectangle(
			w + 50,
			h / 2,
			100,
			h + 100,
			wallOptions
		);

		const mouse = Mouse.create(renderer.canvas);
		const mouseControl = MouseConstraint.create(engine, {
			mouse: mouse,
			constraint: {
				render: {
					visible: false,
				},
			},
		});

		// Add mouse control to the world
		World.add(engine.world, mouseControl);

		const intialShapes = Composites.stack(
			w / 7,
			0,
			5,
			8,
			w / 7,
			10,
			function (x, y) {
				return createShape(x, y);
			}
		);

		// Removed mouse following object
		// const mouseBody = Bodies.circle(0, 0, 16, {
		//     isSensor: false,
		//     isStatic: true,
		//     render: {
		//         visible: true,
		//         fillStyle: "white",
		//         frictionAir: 1,
		//     }
		// });

		World.add(engine.world, [
			buttonWall,
			ground,
			ceiling,
			leftWall,
			rightWall,
			mouseControl,
			intialShapes,
			creatorWall,
			mediaWall,
			wildWall,
			// mouseBody
		]);

		Engine.run(engine);
		Render.run(renderer);

		const eventType = 'ontouchstart' in window ? 'touchend' : 'click';
		// Create burst of shapes from the button click
		buttonRef.current.addEventListener(eventType, function (event) {
			const buttonRect = buttonRef.current.getBoundingClientRect();
			const sectionRect = sectionRef.current.getBoundingClientRect();
			const buttonCenterX =
				buttonRect.left - sectionRect.left + buttonRect.width / 2;
			const buttonCenterY =
				buttonRect.top - sectionRect.top + buttonRect.height / 2;
			const radius = 32;

			for (let i = 0; i < 5; i++) {
				// Calculate angle in radians, offset by -90 degrees so the first shape is at the top
				const angle = (i / 8) * 2 * Math.PI - Math.PI / 2;

				// Calculate x and y position of the shape
				const shapeX = buttonCenterX + radius * Math.cos(angle);
				const shapeY = buttonCenterY + radius * Math.sin(angle);

				// Create the shape and add it to the world
				const shape = createShape(shapeX, shapeY);
				World.add(engine.world, shape);
			}
		});

		// Change gravity on scroll using Lenis velocity
		const handleScroll = throttle(() => {
			let velocity = velocityRef.current * speed * -1 + 0.25;
			velocity = Math.max(-2, Math.min(2, velocity)); // Limit velocity between -4 and 4
			engine.world.gravity.y = velocity;
		}, 200);

		// Resize canvas and update matter bodies on resize window
		const resizeCanvas = () => {
			w = sectionRef.current.offsetWidth;
			h = sectionRef.current.offsetHeight;

			// Manually update the size of the canvas
			renderer.canvas.width = w * window.devicePixelRatio;
			renderer.canvas.height = h * window.devicePixelRatio;
			renderer.canvas.style.width = `${w}px`;
			renderer.canvas.style.height = `${h}px`;

			renderer.options.width = w;
			renderer.options.height = h;

			// Reset the bounds
			renderer.bounds.max.x = w;
			renderer.bounds.max.y = h;

			// Update the positions and sizes of the walls
			Body.setPosition(ground, { x: w / 2, y: h + 50 });
			Body.setPosition(ceiling, { x: w / 2, y: -50 });
			Body.setPosition(leftWall, { x: -50, y: h / 2 });
			Body.setPosition(rightWall, { x: w + 50, y: h / 2 });

			Body.setVertices(
				ground,
				Bodies.rectangle(w / 2, h + 50, w + 100, 100, wallOptions)
					.vertices
			);
			Body.setVertices(
				ceiling,
				Bodies.rectangle(w / 2, -50, w + 100, 100, wallOptions).vertices
			);
			Body.setVertices(
				leftWall,
				Bodies.rectangle(-50, h / 2, 100, h + 100, wallOptions).vertices
			);
			Body.setVertices(
				rightWall,
				Bodies.rectangle(w + 50, h / 2, 100, h + 100, wallOptions)
					.vertices
			);
		};

		// Removed mouse body event
		// document.addEventListener("mousemove", function(event){
		//     const containerRect = sectionRef.current.getBoundingClientRect()
		//     const relativeMousePosition = {
		//         x: event.clientX - containerRect.left,
		//         y: event.clientY - containerRect.top
		//     }
		//     Body.setPosition(mouseBody, relativeMousePosition)
		// })

		window.addEventListener('scroll', handleScroll, { passive: true });
		window.addEventListener('resize', resizeCanvas);

		const canvas = renderer.canvas;

		// Disable default touch behavior on canvas
		const preventDefault = (e) => e.preventDefault();
		canvas.addEventListener('touchstart', preventDefault, {
			passive: false,
		});
		canvas.addEventListener('touchmove', preventDefault, {
			passive: false,
		});
		canvas.addEventListener('touchend', preventDefault, { passive: false });

		// Remove on unmount
		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', resizeCanvas);
			// Clear the Matter.js
			World.clear(engine.world, false);
			Engine.clear(engine);
			World.remove(engine.world, mouseControl);

			// ... existing cleanup code ...
		};
	}, []);

	return (
		<section ref={sectionRef} className='bg-grey canvas-target'>
			<div className='homepage-text-intro'>
				<h2>{heading}</h2>
			</div>
			<div className='homepage-subbrand-callout subbrand-callout p-md s-lg'>
				<a
					ref={wildRef}
					href='/the-wild'
					className='card card--subbrand wild sb-wild'
				>
					<div className='card-inner'>
						<span className='titles'>
							<h3>The Wild</h3>
							<div className='arrow-container'>
								<Arrow />
							</div>
						</span>
						<Image
							data={data.wild}
							classes=''
							size='medium'
							animate={false}
						/>
						{/* <div className='media-container landscape'>
                            <img className='cover-media' alt={data.wild.featuredImage.alt} src={data.wild.featuredImage.url} />
                        </div> */}
						<p className='lede'>{data.wild.description}</p>
					</div>
				</a>
				<a
					ref={mediaRef}
					href='/jungle-media'
					className='card card--subbrand media sb-media'
				>
					<div className='card-inner'>
						<span className='titles'>
							<h3>Jungle Media</h3>
							<div className='arrow-container'>
								<Arrow />
							</div>
						</span>
						<Image
							data={data.media}
							classes=''
							size='medium'
							animate={false}
						/>
						{/* <div className='media-container landscape'>
                            <img className='cover-media' alt={data.media.featuredImage.alt} src={data.media.featuredImage.url} />
                        </div> */}
						<p className='lede'>{data.media.description}</p>
					</div>
				</a>
				<a
					ref={creatorRef}
					href='/jungle-creators'
					className='card card--subbrand creators sb-jungle'
				>
					<div className='card-inner'>
						<span className='titles'>
							<h3>Jungle Creators</h3>
							<div className='arrow-container'>
								<Arrow />
							</div>
						</span>
						<Image
							data={data.creators}
							classes=''
							size='medium'
							animate={false}
						/>
						{/* <div className='media-container landscape'>
                            <img className='cover-media' alt={data.creators.featuredImage.alt} src={data.creators.featuredImage.url} />
                        </div> */}
						<p className='lede'>{data.creators.description}</p>
					</div>
				</a>
			</div>
		</section>
	);
};

export default HomepageIntro;
